import axios from "axios";
import {
    LocalDocumentCategory,
    LocalDocumentContentResponse,
    LocalDocumentContentStatus,
    LocalDocumentHistoryResponse,
    LocalDocumentNavigationResponse,
    LocalDocumentOcrVersionResponse,
    LocalDocumentPreviewResponse,
    LocalDocumentsQueryParams,
    LocalDocumentsResponse,
    LocalDocumentsSearchQueryParams,
    LocalDocumentStatisticsResponse,
    OcrVersionPagesWithText
} from "../models/local-documents.models";

interface NavigationPagesProps {
    id: number;
    params: {
        fileName?: string;
        pesel?: number | string;
        dateFrom?: string;
        dateTo?: string;
        status?: LocalDocumentContentStatus;
        type?: string;
        packageId?: string;
        sortColumn?: string;
        sortOrder?: string;
    }
}

export const localDocumentsService = {
    fetchDocuments : async ({
        pageNumber = 0,
        pageSize = 50,
        sortColumn,
        sortOrder,
    }: LocalDocumentsQueryParams = {}): Promise<LocalDocumentsResponse> => {
        const response = await axios.get<LocalDocumentsResponse>('/processing/documents', {
            params: { pageNumber, pageSize, sortColumn, sortOrder }
        });
        return response.data;
    },

    fetchStatus: async (): Promise<LocalDocumentContentStatus> => {
        const response = await axios.get<LocalDocumentContentStatus>('/local-document/status');
        return response.data;
    },

    fetchDocumentsProcessedSearch: async ({
        fileName,
        pesel,
        dateFrom,
        dateTo,
        status,
        type,
        packageId,
        sortColumn,
        sortOrder,
        pageNumber,
        pageSize,
    }: LocalDocumentsSearchQueryParams = {}): Promise<LocalDocumentsResponse> => {
        const response = await axios.get<LocalDocumentsResponse>('/processing/documents/search', {
            params: {
                fileName,
                pesel,
                dateFrom,
                dateTo,
                status,
                type,
                packageId,
                sortColumn,
                sortOrder,
                pageNumber,
                pageSize
            }
        });

        return response.data;
    },

    fetchDocument: async (id: string): Promise<LocalDocumentContentResponse> => {
        const response = await axios.get<LocalDocumentContentResponse>(`/processing/documents/${id}`);
        return response.data;
    },

    exportLocalDocumentsToCSV: async (localDocumentIds: number[]): Promise<Blob> => {
        const requestBody = { localDocumentIds: localDocumentIds };
        const response = await axios.post<Blob>('/local-document/processed/export', requestBody, {
            responseType: 'blob',
        });

        return response.data;
    },

    fetchHistory: async (id: number): Promise<LocalDocumentHistoryResponse[]> => {
        const response = await axios.get<LocalDocumentHistoryResponse[]>(`/processing/documents/${id}/history`);
        return response.data;
    },

    previewDocumentData: async (id: number): Promise<LocalDocumentPreviewResponse> => {
        const response = await axios.get<LocalDocumentPreviewResponse>(`/processing/documents/${id}/preview`);
        return response.data;
    },

    fetchOcrVersion: async (id: number): Promise<LocalDocumentOcrVersionResponse> => {
        const response = await axios.get<LocalDocumentOcrVersionResponse>(`/processing/documents/${id}/ocr`);
        return response.data;
    },

    fetchOriginalVersion: async (id: number): Promise<Uint8Array> => {
        const response = await axios.get(
            `/processing/documents/${id}/original`,
            { responseType: 'arraybuffer' }
        );
        return new Uint8Array(response.data);
    },

    fetchOriginalPages: async (id: number, pagesNumber: number): Promise<string> => {
        const response = await axios.get<string>(`/processing/documents/${id}/original/pages/${pagesNumber}`);
        return response.data;
    },

    fetchNavigationPages: async ({id, params}: NavigationPagesProps): Promise<LocalDocumentNavigationResponse> => {
        const response = await axios.get<LocalDocumentNavigationResponse>(`/processing/documents/${id}/navigation`, { params: params });
        return response.data;
    },

    approveDocument: async (id: number): Promise<LocalDocumentContentResponse> => {
        const response = await axios.patch<LocalDocumentContentResponse>(`/processing/documents/${id}/approve`);
        return response.data;
    },

    approveOcrResultChanges: async (id: number, requestBody: OcrVersionPagesWithText): Promise<LocalDocumentOcrVersionResponse> => {
        const response = await axios.patch<LocalDocumentOcrVersionResponse>(`/processing/documents/${id}/ocr`, requestBody);
        return response.data;
    },

    updateDocumentCategory: async (id: number, category: LocalDocumentCategory): Promise<LocalDocumentContentResponse> => {
        const response = await axios.patch<LocalDocumentContentResponse>(`/processing/documents/${id}/type`, { type: category });
        return response.data;
    },

    fetchStatistics: async (): Promise<LocalDocumentStatisticsResponse> => {
        const response = await axios.get<LocalDocumentStatisticsResponse>('/statistics');
        return response.data;
    },

    exportStatisticsToPdf: async (language: string): Promise<Blob> => {
        const response = await axios.get<Blob>(`/statistics/export`, {
                params: { lang: language },
                responseType: 'blob',
            });
        return response.data;
    }
};